import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Button } from "../../../components/ui/button";
import { useNavigate } from "react-router-dom";
import { type FunctionComponent, useEffect, useReducer } from "react";
import CreateCampaignPreview from "../../../components/create-campaign-preview.components";
import {
  type IzCampaignFormAdmin,
  zCampaignFormAdmin,
} from "../../../z/admin/campaign-form.admin.z";
import type {
  ICampaign,
  IProductRefund,
} from "../../../types/models/campaign.models.types";
import { SocialGender } from "../../../types/models/social.models.types";
import CampaignFormAdminPartial from "../campaign-form.admin.partials";
import { useUpdateCampaignByIdAdminHttp } from "../../../hooks/http/admin/campaigns/update-campaign-by-id.admin.http";
import { useMakeCampaignLiveAdminHttp } from "../../../hooks/http/admin/campaigns/make-campaign-live.admin.http";
import { useToast } from "../../../hooks/use-toast";
import { useCreateAssetCommonHttp } from "../../../hooks/http/common/utils/asset/create-asset.common.http";
import Spinner from "../../../components/spinner.components";
import type { IAssetMinimal } from "../../../types/models/asset.models.types";
import {
  findErrorMessage,
  handleHttpError,
  sanitizeNumberField,
} from "../../../utils/utils";
import ShareIcon from "../../../assets/svgs/common/share.svg";
import { MilestoneRewardType } from "../../../z/brand/campaign-form.brand.z";

interface IProps {
  campaign: ICampaign;
}

export const EditCampaignAdminPartial: FunctionComponent<IProps> = ({
  campaign,
}) => {
  const [, forceUpdate] = useReducer((x) => x + 1, 0);
  const { execute: updateCampaignHttp, fetching: updatingCampaign } =
    useUpdateCampaignByIdAdminHttp();
  const { execute: makeCampaignLiveHttp, fetching: makingCampaignLive } =
    useMakeCampaignLiveAdminHttp();
  const { execute: uploadAssetHttp, fetching: uploadingAsset } =
    useCreateAssetCommonHttp();

  const navigate = useNavigate();
  const { toast } = useToast();

  const form = useForm<IzCampaignFormAdmin>({
    resolver: zodResolver(zCampaignFormAdmin),
    defaultValues: {
      name: campaign.name,
      coverImage: campaign.coverImage,
      coverImageFile: [],
      socialPlatform: campaign.socialPlatform,
      favouriteSocial: campaign.favouriteSocial?.id || null,
      socialPostType: campaign.socialPostType,
      socialCreatorType: campaign.socialCreatorType,
      genderPreference: campaign.genderPreference || SocialGender.prefernotsay,
      minimumFollowers: campaign.minimumFollowers,
      maximumFollowers: campaign.maximumFollowers,
      locationPreference: campaign.locationPreference
        ? campaign.locationPreference
        : [],
      startDate: campaign.startDate ? new Date(campaign.startDate) : null,
      endDate: campaign.endDate ? new Date(campaign.endDate) : null,
      genre: campaign.genre || [],
      budget: campaign.budget,
      budgetInternal: campaign.budgetInternal || null,
      milestones: campaign.milestones || [],
      links: campaign.links,
      milestoneHasRefund: campaign.milestoneHasRefund,
      productRefund: {
        image: campaign.productRefund ? [campaign.productRefund.image] : [],
        link: campaign.productRefund?.link || "",
        couponCode: campaign.productRefund?.couponCode || null,
        refundAmount: campaign.productRefund?.refundAmount || null,
        enabled: campaign.productRefund != null,
      },
      hasFixedPayout: campaign.hasFixedPayout,
      assets: campaign.assets,
      assetFile: [],
      sampleVideos: campaign.sampleVideos,
      sampleVideosFiles: [],
      briefVideos: campaign.briefVideos,
      briefVideosFiles: [],
      hashtags: campaign.hashtags,
      mentions: campaign.mentions,
      aboutCampaign: campaign.aboutCampaign,
      do: campaign.do,
      dont: campaign.dont,
      aboutPayment: campaign.aboutPayment,
      aboutBrand: campaign.aboutBrand,
    },
  });

  async function uploadAsset(file: File) {
    const formData = new FormData();
    formData.append("file", file);
    const response = await uploadAssetHttp({
      body: formData,
    });
    if (!response.success) {
      return false;
    }
    return response.data.asset;
  }

  console.log(form.getValues("genre"));

  async function onSubmit(data: IzCampaignFormAdmin) {
    try {
      const genderPreference =
        data.genderPreference === SocialGender.prefernotsay
          ? null
          : data.genderPreference;

      if (
        form.formState.dirtyFields.coverImageFile &&
        data.coverImageFile.length > 0
      ) {
        const uploadedImage = await uploadAsset(data.coverImageFile[0].file);

        if (!uploadedImage) {
          toast({
            content: "Failed to upload cover image. Please try again",
            toastType: "error",
          });
          return;
        }

        form.setValue(
          "coverImage",
          {
            id: uploadedImage.id,
            url: uploadedImage.url,
          },
          { shouldDirty: true },
        );
        form.setValue(
          "coverImage",
          {
            id: uploadedImage.id,
            url: uploadedImage.url,
          },
          { shouldDirty: true },
        );

        data.coverImage = {
          id: uploadedImage.id,
          url: uploadedImage.url,
        };
      }
      if (form.formState.dirtyFields.assetFile && data.assetFile.length > 0) {
        const uploadedAssets = await Promise.all(
          data.assetFile.map((file) => uploadAsset(file)),
        );
        const failedAssetUploads = uploadedAssets.filter((asset) => !asset);

        if (failedAssetUploads.length > 0) {
          toast({
            content: "Failed to upload some assets. Please try again",
            toastType: "error",
          });
          return;
        }

        const newAssets = uploadedAssets
          .filter((asset): asset is IAssetMinimal => asset !== false)
          .map((asset) => ({
            id: asset.id,
            url: asset.url,
          }));

        form.setValue("assets", [...newAssets, ...data.assets], {
          shouldDirty: true,
        });
        data.assets = [...newAssets, ...data.assets];
      }
      if (
        form.formState.dirtyFields.sampleVideosFiles &&
        data.sampleVideosFiles.length > 0
      ) {
        const uploadedSampleVideos = await Promise.all(
          data.sampleVideosFiles.map((file) => uploadAsset(file)),
        );
        const failedUploads = uploadedSampleVideos.filter((video) => !video);

        if (failedUploads.length > 0) {
          toast({
            content: "Failed to upload some sample videos. Please try again",
            toastType: "error",
          });
          return;
        }

        const newSampleVideos = uploadedSampleVideos
          .filter((video): video is IAssetMinimal => video !== false)
          .map((video) => ({
            id: video.id,
            url: video.url,
          }));

        form.setValue(
          "sampleVideos",
          [...newSampleVideos, ...data.sampleVideos],
          {
            shouldDirty: true,
          },
        );

        data.sampleVideos = [...newSampleVideos, ...data.sampleVideos];
      }
      if (
        form.formState.dirtyFields.briefVideosFiles &&
        data.briefVideosFiles.length > 0
      ) {
        const uploadedBriefVideos = await Promise.all(
          data.briefVideosFiles.map((file) => uploadAsset(file)),
        );
        const failedUploads = uploadedBriefVideos.filter((video) => !video);

        if (failedUploads.length > 0) {
          toast({
            content: "Failed to upload some brief videos. Please try again",
            toastType: "error",
          });
          return;
        }

        const newBriefVideos = uploadedBriefVideos
          .filter((video): video is IAssetMinimal => video !== false)
          .map((video) => ({
            id: video.id,
            url: video.url,
          }));

        form.setValue("briefVideos", [...newBriefVideos], {
          shouldDirty: true,
        });

        data.briefVideos = [...newBriefVideos];
      }
      if (
        form.formState.dirtyFields.productRefund &&
        data.productRefund?.productImageFile &&
        data.productRefund.productImageFile.length > 0
      ) {
        const uploadedProductImage = await Promise.all(
          data.productRefund?.productImageFile.map((file) => uploadAsset(file)),
        );

        const failedUploads = uploadedProductImage.filter((video) => !video);

        if (failedUploads.length > 0) {
          toast({
            content: "Failed to upload product image. Please try again",
            toastType: "error",
          });
          return;
        }
        const newImages = uploadedProductImage
          .filter((image): image is IAssetMinimal => image !== false)
          .map((video) => ({
            id: video.id,
            url: video.url,
            name: video.name,
          }));

        form.setValue("productRefund.image", newImages, {
          shouldDirty: true,
        });

        if (data.productRefund) {
          data.productRefund.image = [...newImages];
        }
      }

      let productRefund: IProductRefund | null | undefined = undefined;

      if (form.formState.dirtyFields.productRefund) {
        if (data.productRefund?.enabled) {
          const image = data.productRefund.image.at(0);
          if (!image) {
            throw new Error("image not found");
          }

          productRefund = {
            image: image,
            link: data.productRefund.link,
            couponCode: data.productRefund.couponCode,
            refundAmount: data.productRefund.refundAmount,
          };
        } else {
          productRefund = null;
        }
      }

      const response = await updateCampaignHttp({
        pathParams: {
          id: campaign.id,
        },
        body: {
          name: form.formState.dirtyFields.name ? data.name : undefined,
          coverImage: form.formState.dirtyFields.coverImage
            ? data.coverImage
              ? data.coverImage.id
              : undefined
            : undefined,
          socialPlatform: form.formState.dirtyFields.socialPlatform
            ? data.socialPlatform
            : undefined,
          socialPostType: form.formState.dirtyFields.socialPostType
            ? data.socialPostType
            : undefined,
          favouriteSocial: form.formState.dirtyFields.favouriteSocial
            ? data.favouriteSocial
            : undefined,
          socialCreatorType: form.formState.dirtyFields.socialCreatorType
            ? data.socialCreatorType
            : undefined,
          genderPreference: form.formState.dirtyFields.genderPreference
            ? genderPreference
            : undefined,
          minimumFollowers: form.formState.dirtyFields.minimumFollowers
            ? sanitizeNumberField(data.minimumFollowers)
            : undefined,
          hasFixedPayout: form.formState.dirtyFields.hasFixedPayout
            ? data.hasFixedPayout.toString()
            : undefined,
          maximumFollowers: form.formState.dirtyFields.maximumFollowers
            ? sanitizeNumberField(data.maximumFollowers)
            : undefined,
          genre: form.formState.dirtyFields.genre
            ? data.genre.map((i) => i.id)
            : undefined,
          locationPreference: form.formState.dirtyFields.locationPreference
            ? data.locationPreference.length > 0
              ? data.locationPreference.map((i) => i.placeId)
              : null
            : undefined,
          startDate: form.formState.dirtyFields.startDate
            ? data.startDate
            : undefined,
          endDate: form.formState.dirtyFields.endDate
            ? data.endDate
            : undefined,
          budget: form.formState.dirtyFields.budget ? data.budget : undefined,
          budgetInternal: form.formState.dirtyFields.budgetInternal
            ? data.budgetInternal
            : undefined,
          milestoneRewardType: MilestoneRewardType.CASH,
          milestones: form.formState.dirtyFields.milestones
            ? data.milestones
            : undefined,
          links: form.formState.dirtyFields.links ? data.links : undefined,
          productRefund: productRefund
            ? {
                image: productRefund.image.id,
                link: productRefund.link,
                couponCode: productRefund.couponCode,
                refundAmount: productRefund.refundAmount,
              }
            : productRefund,
          highestMilestoneOnlyForTopPost: "true",
          assets: form.formState.dirtyFields.assets
            ? data.assets.map((i) => i.id)
            : undefined,
          sampleVideos: form.formState.dirtyFields.sampleVideos
            ? data.sampleVideos.map((i) => i.id)
            : undefined,
          briefVideos: form.formState.dirtyFields.briefVideos
            ? data.briefVideos.map((i) => i.id)
            : undefined,
          hashtags: form.formState.dirtyFields.hashtags
            ? data.hashtags
            : undefined,
          mentions: form.formState.dirtyFields.mentions
            ? data.mentions
            : undefined,
          aboutCampaign: form.formState.dirtyFields.aboutCampaign
            ? data.aboutCampaign
            : undefined,
          do: form.formState.dirtyFields.do ? data.do : undefined,
          dont: form.formState.dirtyFields.dont ? data.dont : undefined,
          aboutPayment: form.formState.dirtyFields.aboutPayment
            ? data.aboutPayment
            : undefined,
          aboutBrand: form.formState.dirtyFields.aboutBrand
            ? data.aboutBrand
            : undefined,
        },
      });
      if (!response.success) {
        return handleHttpError(response, toast);
      }
      if (form.formState.dirtyFields.sampleVideos) {
        form.setValue("sampleVideosFiles", []);
        form.setValue("sampleVideos", response.data.campaign.sampleVideos);
        console.log(form.getValues("sampleVideos"));
      }
      if (form.formState.dirtyFields.assets) {
        form.setValue("assetFile", []);
        form.setValue("assets", response.data.campaign.assets);
      }
      if (form.formState.dirtyFields.briefVideos) {
        form.setValue("briefVideosFiles", []);
        form.setValue("briefVideos", response.data.campaign.briefVideos);
      }
      if (form.formState.dirtyFields.productRefund) {
        form.setValue("productRefund.productImageFile", []);
        // form.setValue("productRefund", response.data.campaign.productRefund);
      }
      toast({
        content: "Campaign updated successfully",
        toastType: "success",
      });

      form.reset({}, { keepValues: true });
      forceUpdate();
    } catch (error) {
      toast({
        content: "Failed to update campaign",
        toastType: "error",
      });
    }
  }

  async function makeLive() {
    const response = await makeCampaignLiveHttp({
      body: null,
      pathParams: {
        id: campaign.id,
      },
    });

    if (!response.success) {
      return handleHttpError(response, toast);
    }

    navigate("/");
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (form.formState.errors) {
      const key = Object.keys(form.formState.errors)[0];

      if (key) {
        const error =
          form.formState.errors[key as keyof typeof form.formState.errors];
        const message = findErrorMessage(error) || "An error occurred";

        toast({
          content: message,
          toastType: "error",
        });
      }
    }
  }, [form.formState.errors]);

  const isDirty = Object.keys(form.formState.dirtyFields).length > 0;

  return (
    <div className="flex flex-col px-6 pl-8 w-full">
      <div className="top-0 z-50 sticky flex justify-between items-center bg-gradient-to-b from-background via-background to-transparent pt-12 w-full">
        <p className="font-semibold text-2xl">Edit Campaign</p>
        <div className="flex gap-4">
          <Button
            onClick={() => {
              navigator.clipboard.writeText(
                `${window.location.origin}/campaign-brief/${campaign.id}`,
              );
              toast({
                content: "Brief link copied",
                toastType: "success",
              });
            }}
            className="gap-1 bg-tertiarybuttonbg border border-border h-12 font-semibold text-base it"
          >
            <ShareIcon />
            Share brief
          </Button>
          <Button
            disabled={makingCampaignLive}
            className={`${isDirty ? "" : "bg-primarybuttonbg"} h-12 w-56 flex items-center justify-center font-semibold text-base text-primarybuttontext`}
            onClick={async () => {
              if (!isDirty) {
                await makeLive();
              }
            }}
          >
            {makingCampaignLive ? <Spinner width={20} /> : "Make campaign live"}
          </Button>
          <Button
            disabled={updatingCampaign || uploadingAsset}
            className={`${isDirty ? "bg-primarybuttonbg" : ""} h-12 w-40 flex items-center justify-center  font-semibold text-base text-primarybuttontext`}
            onClick={async () => {
              if (isDirty) {
                document.getElementById("campaign-form-submit-admin")?.click();
              }
            }}
          >
            {updatingCampaign || uploadingAsset ? (
              <Spinner width={20} />
            ) : (
              "Save Changes"
            )}
          </Button>
        </div>
      </div>
      <div className="flex">
        <div className="relative w-72 min-h-full">
          <div className="fixed">
            <CreateCampaignPreview campaignForm={form} />
          </div>
        </div>

        <div className="flex flex-col flex-1 my-12 ml-6">
          <CampaignFormAdminPartial
            campaign={campaign}
            form={form}
            onSubmit={onSubmit}
          />
        </div>
      </div>
    </div>
  );
};
